import React, { ElementType, useEffect } from 'react';
import Pagination from '@mui/material/Pagination';
import styled from '@mui/material/styles/styled';
import TwSelect, { TwSelectOption } from '../TwSelects/TwSelects';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { SxProps, Theme, useMediaQuery } from '@mui/material';

export interface TwPaginatorProps {
  /**
   * Total number of items to paginate
   */
  items: number;
  /**
   * Number of items per page
   */
  itemsPerPage: number;
  /**
   * Current selected page
   */
  currentPage: number;
  /**
   * Items per page options to display
   */
  itemsPerPageOptions?: TwSelectOption[];
  /**
   * Action to trigger on page change
   */
  onPageChange: (value: number) => void;
  /**
   * Action to trigger when selecting items per page
   */
  selectItemsPerPage?: (value: string) => void;
  /**
   * Sx prop to pass to the container
   */
  sxContainer?: SxProps;
}

const Paginator = styled(Pagination)(() => ({
  padding: '20px 0',
  display: 'flex',
  justifyContent: 'center',

  '& .MuiPaginationItem-root': {
    color: '#6B7280',
    fontWeight: '500',
    fontFamily: 'Poppins'
  },

  '& .MuiPaginationItem-root.Mui-selected': {
    color: '#3A4DE9',
    backgroundColor: 'rgb(58, 77, 233, 0.1)',
    fontFamily: 'Poppins',
    fontWeight: '500',
    fontSize: '14px'
  },

  '& .MuiPagination-ul li:first-child button': {
    marginRight: '20px',

    '&:after': {
      marginLeft: '10px',
      content: "'Prev'"
    }
  },
  '& .MuiPagination-ul li:last-child button': {
    marginLeft: '20px',

    '&:before': {
      marginRight: '10px',
      content: "'Next'"
    }
  }
}));

const TwPaginator: React.FC<TwPaginatorProps> = ({
  items,
  itemsPerPage,
  currentPage,
  itemsPerPageOptions,
  sxContainer,
  onPageChange,
  selectItemsPerPage
}) => {
  const [page, setPage] = React.useState(1);

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleChange = (event: React.ChangeEvent<unknown>, value: number): void => {
    onPageChange(value);
  };

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  return (
    <Stack sx={sxContainer}>
      <Paginator
        count={Math.ceil(items / itemsPerPage)}
        page={page}
        shape="rounded"
        onChange={handleChange}
      />
      {itemsPerPageOptions && itemsPerPageOptions?.length > 0 && (
        <TwSelect
          label="items per page"
          name="items-per-page"
          value={itemsPerPage.toString()}
          optionList={itemsPerPageOptions || []}
          onInputChange={(e): void => {
            selectItemsPerPage && selectItemsPerPage(e.target.value);
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            position: matchesMobileOrTablet ? 'relative' : 'absolute',
            right: '0',
            paddingTop: '20px',
            alignItems: 'center',
            width: '200px',
            '& .tw-selects__label': {
              order: 2
            }
          }}
          selectSx={{
            maxWidth: '80px',
            marginRight: '10px',
            order: 1
          }}
        />
      )}
    </Stack>
  );
};

export default TwPaginator;
